import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DESIGN_CARD_MAX_MEDIA,
  DESIGN_CARD_MEDIA_ENTITY_TYPE,
  DESIGN_CARD_NOTES_MAX_LENGTH,
  DESIGN_CARD_STATUSES,
  DESIGN_CARD_TITLE_MAX_LENGTH,
  type DesignBoardOwner,
  type DesignCard,
  type DesignCardStatus,
  type OrderItem,
} from '@tailoring-crm/shared';
import {
  useCreateDesignCard,
  useUpdateDesignCard,
} from '../../lib/design-board/use-design-board.js';
import { MediaUploader } from '../media/media-uploader.js';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Textarea,
  Label,
} from '@tailoring-crm/ui';

/**
 * One dialog for both creating and editing a design card.
 *
 * The images take two different paths depending on which this is. On CREATE the uploader has no
 * parent yet (`entityId: null`), so its `onChange` only ever hands back an optimistic local list;
 * those ids ride along on the create request as `mediaIds`, and `useCreateDesignCard` is what
 * then binds the uploaded images to the new card id with `attachMedia`. On EDIT the uploader
 * already has the card's id, so every add, remove or reorder inside it patches the card's
 * `mediaIds` immediately — independent of the Save button below, which only ever carries the
 * form fields.
 */
export interface DesignCardDialogProps {
  mode: 'create' | 'edit';
  /** Which board the card is being written to — an order's or a customer's. */
  owner: DesignBoardOwner;
  card: DesignCard | null;
  /** Only an order has garments to choose between; empty on a person's board. */
  orderItems: readonly OrderItem[];
  onClose: () => void;
}

export function DesignCardDialog({
  mode,
  owner,
  card,
  orderItems,
  onClose,
}: DesignCardDialogProps): ReactElement {
  const { t } = useTranslation();
  const create = useCreateDesignCard();
  const update = useUpdateDesignCard();

  const editing = mode === 'edit' && card !== null;

  const [title, setTitle] = useState(() => card?.title ?? '');
  const [orderItemId, setOrderItemId] = useState(() => card?.orderItemId ?? '');
  const [notes, setNotes] = useState(() => card?.notes ?? '');
  const [status, setStatus] = useState<DesignCardStatus>(() => card?.status ?? 'idea');
  const [draftMediaIds, setDraftMediaIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = title.trim().length > 0;

  const submit = async (): Promise<void> => {
    setError(null);
    try {
      if (editing) {
        await update.mutateAsync({
          cardId: card.id,
          patch: {
            title: title.trim(),
            orderItemId: orderItemId || null,
            notes: notes.trim() === '' ? null : notes.trim(),
            status,
          },
        });
      } else {
        await create.mutateAsync({
          owner,
          input: {
            title: title.trim(),
            orderItemId: orderItemId || null,
            notes: notes.trim() === '' ? null : notes.trim(),
            status,
            mediaIds: draftMediaIds,
          },
        });
      }
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('error.unexpected'));
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {editing ? t('designBoard.editTitle') : t('designBoard.newDesign')}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          {error ? (
            <p
              role="alert"
              className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive"
            >
              {error}
            </p>
          ) : null}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="design-card-title">{t('designBoard.field.title')}</Label>
            <Input
              id="design-card-title"
              value={title}
              maxLength={DESIGN_CARD_TITLE_MAX_LENGTH}
              autoFocus
              onChange={(event) => setTitle(event.target.value)}
            />
          </div>

          {/* There is no garment to point at until something is booked — the API refuses an
              `orderItemId` on a person's board for the same reason. */}
          {owner.kind === 'order' ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="design-card-garment">{t('designBoard.field.garment')}</Label>
              <select
                id="design-card-garment"
                value={orderItemId}
                onChange={(event) => setOrderItemId(event.target.value)}
                className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
              >
                <option value="">{t('designBoard.wholeOrder')}</option>
                {orderItems.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.garmentTypeLabel}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="design-card-status">{t('designBoard.field.status')}</Label>
            <select
              id="design-card-status"
              value={status}
              onChange={(event) => setStatus(event.target.value as DesignCardStatus)}
              className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
            >
              {DESIGN_CARD_STATUSES.map((one) => (
                <option key={one} value={one}>
                  {t(`designBoard.status.${one}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="design-card-notes">{t('designBoard.field.notes')}</Label>
            <Textarea
              id="design-card-notes"
              rows={3}
              value={notes}
              maxLength={DESIGN_CARD_NOTES_MAX_LENGTH}
              onChange={(event) => setNotes(event.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>{t('designBoard.field.images')}</Label>
            <MediaUploader
              entityType={DESIGN_CARD_MEDIA_ENTITY_TYPE}
              entityId={editing ? card.id : null}
              maxFiles={DESIGN_CARD_MAX_MEDIA}
              onChange={(uploaded) => {
                const ids = uploaded.map((item) => item.id);
                if (editing) {
                  update.mutate({ cardId: card.id, patch: { mediaIds: ids } });
                } else {
                  setDraftMediaIds(ids);
                }
              }}
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            disabled={!canSubmit || create.isPending || update.isPending}
            onClick={() => void submit()}
          >
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
